/**
 * Client-side image compression — runs BEFORE upload to cut bandwidth dramatically.
 * Uses canvas to:
 *   - Downscale anything wider than MAX_DIM to MAX_DIM (preserves aspect ratio)
 *   - Re-encode JPEG at quality 0.85 (visually indistinguishable for photo content)
 *
 * Typical results:
 *   - 4032x3024 / 8.5 MB phone photo → 2400x1800 / ~1.2 MB
 *   - 6000x4000 / 22 MB DSLR raw → 2400x1600 / ~1.5 MB
 *
 * The backend already produces its own WebP variants at thumb/medium/large/original sizes,
 * so the client only needs to ship a "good enough" 2400px reference. Anything bigger is wasted bandwidth.
 *
 * Files smaller than SKIP_BELOW_BYTES or narrower than MAX_DIM are returned untouched.
 * On any error, falls back to the original file (never blocks the upload).
 */

const MAX_DIM = 2400;
const QUALITY = 0.85;
const SKIP_BELOW_BYTES = 800 * 1024; // 800 KB — under this, just upload as-is

export async function compressImage(file) {
  try {
    if (!file || !file.type || !file.type.startsWith("image/")) return file;
    // PNG with transparency, GIF, SVG, AVIF → don't touch (avoid quality loss / format mangling)
    if (!/^image\/(jpe?g|webp|heic|heif)$/i.test(file.type)) return file;
    if (file.size <= SKIP_BELOW_BYTES) return file;

    const bmp = await loadBitmap(file);
    if (!bmp) return file;
    const { width, height } = bmp;
    if (width <= MAX_DIM) {
      if (typeof bmp.close === "function") bmp.close();
      return file;
    }
    const ratio = MAX_DIM / width;
    const newW = MAX_DIM;
    const newH = Math.round(height * ratio);

    const canvas = document.createElement("canvas");
    canvas.width = newW;
    canvas.height = newH;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bmp, 0, 0, newW, newH);
    if (typeof bmp.close === "function") bmp.close();

    const blob = await new Promise((res) =>
      canvas.toBlob(res, "image/jpeg", QUALITY)
    );
    if (!blob || blob.size >= file.size) return file; // didn't actually help

    // Preserve a sensible filename so the server-side ext sniff works
    const base = (file.name || "image").replace(/\.[^.]+$/, "");
    return new File([blob], `${base}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file; // never block upload on a compression error
  }
}

async function loadBitmap(file) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      // fall through to <img> path (HEIC/Safari sometimes needs it)
    }
  }
  return await new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve(img);
      // URL revoked after consumer is done — small leak ok vs. wasted decode
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}
